import { useCallback, useEffect, useState } from "react";
import { API } from "../../../Networking/API";

const DRIVE_STATUS_SORT = { onGoing: 0, pending: 1, completed: 2 };

function normalizeEmail(email) {
  return email?.trim().toLowerCase() || "";
}

function formatDriveStatus(status) {
  if (!status) return "";
  const key = String(status);
  if (key === "onGoing") return "Ongoing";
  if (key === "pending") return "Upcoming";
  if (key === "completed") return "Completed";
  return key.charAt(0).toUpperCase() + key.slice(1);
}

function mapDriveRow(row) {
  return {
    id: String(row.id),
    name: row.drive_name || `Drive ${row.id}`,
    runningStatus: row.drive_running_status || "",
    active: row.status !== 2,
  };
}

function sortDrives(driveList) {
  return [...driveList].sort((a, b) => {
    const orderA = DRIVE_STATUS_SORT[a.runningStatus] ?? 99;
    const orderB = DRIVE_STATUS_SORT[b.runningStatus] ?? 99;
    if (orderA !== orderB) return orderA - orderB;
    return a.name.localeCompare(b.name);
  });
}

function pickDefaultDriveId(driveList) {
  if (!driveList.length) return "";

  const activeOngoing = driveList.find(
    (d) => d.runningStatus === "onGoing" && d.active
  );
  if (activeOngoing) return activeOngoing.id;

  const anyOngoing = driveList.find((d) => d.runningStatus === "onGoing");
  if (anyOngoing) return anyOngoing.id;

  return driveList[0].id;
}

export function useOngoingDriveFilter(setNotify) {
  const [drives, setDrives] = useState([]);
  const [selectedDriveId, setSelectedDriveId] = useState("");
  const [driveEmails, setDriveEmails] = useState(() => new Set());
  const [loadingDrives, setLoadingDrives] = useState(true);
  const [loadingCandidates, setLoadingCandidates] = useState(false);

  const loadDriveCandidates = useCallback(async (driveId) => {
    if (!driveId) {
      setDriveEmails(new Set());
      return;
    }

    setLoadingCandidates(true);
    try {
      const response = await API.getCandidateOnDrive(driveId);
      const rows =
        response?.data?.status === true && Array.isArray(response.data.data?.rows)
          ? response.data.data.rows
          : [];

      const emails = new Set(
        rows.map((row) => normalizeEmail(row.email)).filter(Boolean)
      );
      setDriveEmails(emails);
    } catch {
      setDriveEmails(new Set());
      setNotify?.({
        isOpen: true,
        message: "Failed to load candidates for the selected drive",
        type: "error",
      });
    } finally {
      setLoadingCandidates(false);
    }
  }, [setNotify]);

  useEffect(() => {
    let cancelled = false;

    const loadAllDrives = async () => {
      setLoadingDrives(true);
      try {
        const response = await API.getDriveData();
        if (cancelled) return;

        const rows =
          response?.data?.status === true &&
          Array.isArray(response?.data?.data?.rows)
            ? response.data.data.rows
            : [];

        const allDrives = sortDrives(
          rows
            .filter((d) => d.status !== 2)
            .map(mapDriveRow)
        );

        setDrives(allDrives);
        setSelectedDriveId(pickDefaultDriveId(allDrives));
        if (allDrives.length === 0) {
          setDriveEmails(new Set());
        }
      } catch {
        if (!cancelled) {
          setDrives([]);
          setSelectedDriveId("");
          setDriveEmails(new Set());
          setNotify?.({
            isOpen: true,
            message: "Failed to load drives",
            type: "error",
          });
        }
      } finally {
        if (!cancelled) setLoadingDrives(false);
      }
    };

    loadAllDrives();
    return () => {
      cancelled = true;
    };
  }, [setNotify]);

  useEffect(() => {
    if (selectedDriveId) {
      loadDriveCandidates(selectedDriveId);
    }
  }, [selectedDriveId, loadDriveCandidates]);

  const selectedDrive =
    drives.find((d) => d.id === selectedDriveId) || null;

  return {
    drives,
    selectedDriveId,
    setSelectedDriveId,
    selectedDrive,
    driveEmails,
    loadingDrives,
    loadingCandidates,
    hasDrives: drives.length > 0,
    formatDriveStatus,
  };
}

export function interviewBelongsToDrive(interview, driveEmails) {
  if (!driveEmails?.size) return false;
  return driveEmails.has(normalizeEmail(interview?.user_email));
}

export function candidateBelongsToDrive(row, driveEmails) {
  if (!driveEmails?.size) return false;
  return driveEmails.has(normalizeEmail(row?.email));
}
